'use client'
import type { PartialMessage } from '@bufbuild/protobuf'
import type { GetPagePublicResponse } from '@fingertip/creator-proto/gen/fingertip/creator/page/v1/page_pb'
import { observer } from 'mobx-react-lite'
import { Suspense, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { BookerSuccessFooterCta } from '@/components/booking/booker/components/booker-success-footer-cta'
import { BookerPage } from '@/components/booking/booker-page'
import { PageFooter } from '@/components/page-footer/page-footer'
import { FooterCta } from '@/components/page-footer/shared/footer-cta'
import { PageHeader } from '@/components/page-header/page-header'
import { PageBackground } from '@/components/public-page/page-background'
import { PageFont } from '@/components/public-page/page-font'
import { PageNavBar } from '@/components/public-page/page-navbar'
import { PageStyle } from '@/components/public-page/page-style'
import { PageView } from '@/components/public-page/page-view'
import { AuthProvider } from '@/lib/hooks/use-token'
import { PHProvider } from '@/lib/posthog/providers'
import { RootStoreContext } from '@/lib/stores/root-store'
import { RootStoreProvider } from '@/lib/stores/root-store-provider'
import { cn } from '@/lib/utils'
import { QueryClientProvider } from '@/lib/utils/provider'

import { PageWhiteShadow } from '../public-page/page-white-shadow'
import { ScaleFactor } from '../public-page/scale-factor'
import { PageMeta } from '../shared/page-meta'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'

type Props = {
  siteSlug: string
  data: PartialMessage<GetPagePublicResponse>
  showNavBar?: boolean
  hasBackground?: boolean
  domain?: string
  mode?: 'page' | 'embed'
  forceMobile?: boolean
}

export const PublicBookingPage = observer(
  ({
    siteSlug,
    data,
    showNavBar = true,
    hasBackground = true,
    domain,
    mode = 'page',
    forceMobile,
  }: Props) => {
    const { t } = useTranslation()
    const {
      bookerStore: { stage },
    } = useContext(RootStoreContext)

    // Determine what to show based on mode
    const isEmbedMode = mode === 'embed'

    return (
      <>
        {data?.site?.customHtmlBodyStart && !isEmbedMode && (
          <div
            dangerouslySetInnerHTML={{ __html: data.site.customHtmlBodyStart }}
          />
        )}

        {hasBackground && !isEmbedMode && (
          <PageBackground content={data?.pageTheme?.content} />
        )}

        <div
          className={cn('relative flex h-full flex-col', {
            'border-0 shadow-none': isEmbedMode,
          })}
        >
          {!isEmbedMode && (
            <>
              <PageStyle content={data?.pageTheme?.content} />
              <PageFont content={data?.pageTheme?.content} />
              <ScaleFactor />
            </>
          )}

          <AuthProvider>
            <QueryClientProvider>
              <RootStoreProvider>
                <PHProvider>
                  <div
                    className={`relative ${isEmbedMode ? '' : 'z-1'} w-full flex-1 ${isEmbedMode ? '' : 'font-page-body font-page-body-weight text-page-text'}`}
                  >
                    <div
                      className={cn({
                        'w-full': isEmbedMode,
                        'relative z-30 mx-auto w-full max-w-[calc(480px*var(--scale-factor))] lg:my-fluid-4':
                          !isEmbedMode,
                      })}
                    >
                      {!isEmbedMode && (
                        <PageHeader
                          hasVerifiedBadge={!!data.site?.verified}
                          page={data?.page}
                          headerSlug={data?.pageTheme?.content?.headerSlug}
                          headerImageStyle={
                            data?.pageTheme?.content?.headerImageStyle
                          }
                          forceMobile={forceMobile}
                        />
                      )}

                      <div className={cn({ 'px-fluid-4': !isEmbedMode })}>
                        {!isEmbedMode && (
                          <div className="mb-fluid-4">
                            <div className="ft-widget-wrapper size-full rounded-page-widget text-neutral-900">
                              <div className="bg-page-widget-background shadow-page-widget border-page-widget p-block size-full rounded-page-widget text-page-body-text backdrop-blur-page-widget">
                                <Breadcrumb>
                                  <BreadcrumbList>
                                    <BreadcrumbItem>
                                      <BreadcrumbLink
                                        href={domain ? '/' : `/${siteSlug}`}
                                        className="text-page-body-text hover:text-page-body-text"
                                      >
                                        {data?.site?.name || siteSlug}
                                      </BreadcrumbLink>
                                    </BreadcrumbItem>

                                    <BreadcrumbSeparator className="text-page-body-text" />

                                    <BreadcrumbItem>
                                      <BreadcrumbPage className="text-page-body-text">
                                        {t('booking')}
                                      </BreadcrumbPage>
                                    </BreadcrumbItem>
                                  </BreadcrumbList>
                                </Breadcrumb>
                              </div>
                            </div>
                          </div>
                        )}

                        <div
                          className={cn(
                            'bg-background p-fluid-4 font-sans font-normal text-foreground',
                            {
                              'rounded-lg': !isEmbedMode,
                            },
                          )}
                        >
                          <BookerPage
                            siteSlug={siteSlug}
                            source={`siteSlug:${siteSlug}`}
                            isEmbedMode={isEmbedMode}
                          />
                        </div>

                        {stage === 'SUCCESS' &&
                          !data?.site?.hideFingertipBranding && (
                            <div
                              className={cn(
                                'font-sans font-normal text-foreground',
                                {
                                  'mt-2': isEmbedMode,
                                  'mt-4 rounded-lg bg-background p-fluid-2':
                                    !isEmbedMode,
                                },
                              )}
                            >
                              <BookerSuccessFooterCta />
                            </div>
                          )}
                      </div>

                      {!isEmbedMode && (
                        <div className="pt-fluid-8 px-fluid-4">
                          <PageFooter
                            page={data?.page}
                            footerSlug={data?.pageTheme?.content?.footerSlug}
                            hideFingertipBranding={
                              data.site?.hideFingertipBranding
                            }
                            siteSlug={siteSlug}
                            domain={domain}
                            forceMobile={forceMobile}
                          >
                            <div className="p-block flex justify-center pt-fluid-8!">
                              <FooterCta siteSlug={siteSlug} />
                            </div>
                          </PageFooter>
                        </div>
                      )}
                    </div>

                    {!isEmbedMode && !forceMobile && <PageWhiteShadow />}
                  </div>

                  {showNavBar && !isEmbedMode && (
                    <PageNavBar
                      siteSlug={siteSlug}
                      pageSlug="index"
                      domain={domain}
                      data={data}
                    />
                  )}
                </PHProvider>
              </RootStoreProvider>
            </QueryClientProvider>
          </AuthProvider>
        </div>

        <PHProvider>
          <Suspense fallback={null}>
            <PageView
              data={{
                page_slug: 'index',
                page_id: data.page?.id,
                site_slug: siteSlug,
                site_id: data.site?.id,
              }}
            />
          </Suspense>
        </PHProvider>

        {!isEmbedMode && <PageMeta site={data?.site} />}
      </>
    )
  },
)
